import React, { useCallback, useEffect, useState } from 'react';
import { essentialsService, DashboardContactRow } from '../../services/essentialsService';
import { crmService } from '../../services/crmService';
import { ListPageHeader } from '../../components/crm/ListPageHeader';
import { CrmEmptyState } from '../../components/crm/EmptyState';
import ContactDetailPanel from '../../components/essentials/ContactDetailPanel';

function dueLabel(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Essentials Tasks = contact follow-ups only (reuses ContactFollowUpReminder /
 * next_follow_up_at). No recurring tasks, dependencies, projects, or templates.
 * Create/reschedule via the shared contact detail; complete via mark-followed-up.
 */
const EssentialsTasks: React.FC = () => {
  const [rows, setRows] = useState<DashboardContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await essentialsService.getDashboard();
      setRows(d.follow_ups_due);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const complete = async (id: string) => {
    await crmService.markClientFollowedUp(id).catch(() => undefined);
    load();
  };

  return (
    <div className="space-y-4">
      <ListPageHeader title="Tasks" subtitle="Follow-ups that are due or overdue." />

      {loading ? (
        <div className="py-12 text-center text-fg-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <CrmEmptyState title="You're all caught up" description="Follow-ups you schedule on a contact will appear here." />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-surface">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="truncate font-medium text-fg">{r.full_name}</div>
                <div className={`text-xs ${r.overdue ? 'font-semibold text-danger-fg' : 'text-fg-muted'}`}>
                  {r.overdue ? 'Overdue · ' : ''}{dueLabel(r.next_follow_up_at)}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button type="button" onClick={() => complete(r.id)} className="btn-primary rounded-lg px-3 py-1.5 text-sm">Complete</button>
                <button type="button" onClick={() => setOpenId(r.id)} className="btn-secondary rounded-lg px-3 py-1.5 text-sm">Reschedule</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {openId ? (
        <ContactDetailPanel contactId={openId} onClose={() => setOpenId(null)} onChanged={() => load()} />
      ) : null}
    </div>
  );
};

export default EssentialsTasks;
